/* eslint-disable @typescript-eslint/no-explicit-any */
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import type { ApiConfig, ApiRequestConfig, ParallelRequest } from './http-interface'
import axios from 'axios'
import { userStore } from '../stores/user'
import { GET_STORAGE } from '../utils/storage'
import { StorageEnum } from '../enums/storage.enum'

export class AppService {
  private instances: AxiosInstance = {} as AxiosInstance

  constructor() {
    this.initializeInstances()
  }

  initializeInstances() {
    const base_url = import.meta.env.VITE_API_URL
    this.instances = axios.create({
      baseURL: base_url,
      timeout: 10000, // 10 segundos
      headers: {
        'Content-Type': 'application/json',
      },
    })
    this.setupInterceptors(this.instances)
  }

  setupInterceptors(instance: AxiosInstance): void {
    // Request interceptor
    instance.interceptors.request.use(
      (config: ApiRequestConfig) => {
        const userState = userStore()

        // Priorizar el token del estado si está disponible
        if (userState && userState.token) {
          config.headers.Authorization = `Bearer ${userState.token}`
        }
        // Si no hay token en el estado, usar el del storage
        else {
          const token = GET_STORAGE(StorageEnum.TOKEN)
          if (token) {
            config.headers.Authorization = `Bearer ${atob(token)}`
          }
        }

        return config
      },
      (error: any) => {
        console.error(` Request Error:`, error)
        return Promise.reject(error)
      },
    )

    // Response interceptor
    instance.interceptors.response.use(
      (response: AxiosResponse) => {
        return response
      },
      async (error: any) => {
        const originalRequest: ApiRequestConfig = error.config

        // Manejo de errores 401 (no autorizado)
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true

          try {
            // Intentar renovar token
            await this.refreshToken()
            const userState = userStore()
            if (originalRequest.headers && userState.token) {
              originalRequest.headers.Authorization = `Bearer ${userState.token}`
            }
            return instance(originalRequest)
          } catch (refreshError) {
            this.handleAuthError()
            return Promise.reject(refreshError)
          }
        }

        if (!error.response) {
          console.error(` Network Error:`, error.message)
        } else {
          console.error(` Response Error:`, {
            status: error.response.status,
            message: error.response.data?.message || error.message,
          })
        }

        return Promise.reject(error)
      },
    )
  }

  setToken(token: string): void {
    Object.values(this.instances).forEach((instance) => {
      instance.defaults.headers.Authorization = `Bearer ${token}`
    })
  }

  clearToken() {
    Object.values(this.instances).forEach((instance) => {
      delete instance.defaults.headers.Authorization
    })
  }

  async refreshToken(): Promise<string> {
    try {
      const response = await this.instances.post<{ token: string }>('/refresh')
      this.setToken(response.data.token)
      return response.data.token
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      throw new Error('Failed to refresh token')
    }
  }

  handleAuthError() {
    this.clearToken()
    if (typeof window !== 'undefined') {
      window.location.href = '/login'
    }
  }

  public get<T = any>(
    endpoint: string,
    config: AxiosRequestConfig = {},
  ): Promise<AxiosResponse<T>> {
    if (!this.instances) {
      throw new Error(`Service not found`)
    }
    return this.instances.get<T>(endpoint, config)
  }

  public post<T = any>(
    endpoint: string,
    data?: any,
    config: AxiosRequestConfig = {},
  ): Promise<AxiosResponse<T>> {
    if (!this.instances) {
      throw new Error(`Service not found`)
    }
    return this.instances.post<T>(endpoint, data, config)
  }

  public put<T = any>(
    endpoint: string,
    data?: any,
    config: AxiosRequestConfig = {},
  ): Promise<AxiosResponse<T>> {
    if (!this.instances) {
      throw new Error(`Service not found`)
    }
    return this.instances.put<T>(endpoint, data, config)
  }

  public delete<T = any>(
    endpoint: string,
    config: AxiosRequestConfig = {},
  ): Promise<AxiosResponse<T>> {
    if (!this.instances) {
      throw new Error(`Service  not found`)
    }
    return this.instances.delete<T>(endpoint, config)
  }

  public getInstance(): AxiosInstance {
    if (!this.instances) {
      throw new Error(`Service  not found`)
    }
    return this.instances
  }

  public async parallel(
    requests: ParallelRequest[],
  ): Promise<PromiseSettledResult<AxiosResponse<any>>[]> {
    const promises = requests.map((req) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
      const method = this[req.method] as Function
      return method.call(this, req.service, req.endpoint, req.data, req.config)
    })
    return Promise.allSettled(promises)
  }

  public updateConfig(service: string, config: Partial<ApiConfig>): void {
    if (this.instances) {
      Object.assign(this.instances.defaults, config)
    }
  }
}
